import type { AppNotification, NotificationCategory } from "./types";

/** Where a notification leads and how to describe that destination. */
export interface NotificationTarget {
  label: string;
  to: string;
}

interface CategoryRoute {
  /** Detail route for one record; absent when the category has no detail page. */
  detail?: (id: string) => string;
  list: string;
  noun: string;
}

/**
 * Routes for each producer table's records. `credit` rows come from two tables
 * (customer credits and credit applications), so `entity_id` cannot address one
 * detail page and the credits list is the destination.
 */
const CATEGORY_ROUTES: Partial<Record<NotificationCategory, CategoryRoute>> = {
  invoice: { detail: (id) => `/invoices/${id}`, list: "/invoices", noun: "invoice" },
  estimate: { detail: (id) => `/estimates/${id}`, list: "/estimates", noun: "estimate" },
  payment: {
    detail: (id) => `/customer-payments/${id}`,
    list: "/customer-payments",
    noun: "payment",
  },
  credit: { list: "/credits", noun: "credits" },
  expense: { detail: (id) => `/expenses/${id}`, list: "/expenses/list", noun: "expense" },
  purchase_order: {
    detail: (id) => `/purchase-orders/${id}`,
    list: "/purchase-orders",
    noun: "purchase order",
  },
};

/**
 * Resolves the in-app destination for a notification. A deleted record has no
 * detail page, so it leads to its category list; `security` and `system`
 * events have no destination.
 */
export function notificationTarget(
  notification: Pick<AppNotification, "action" | "category" | "entityId">,
): NotificationTarget | null {
  const route = CATEGORY_ROUTES[notification.category];
  if (!route) {
    return null;
  }

  if (route.detail && notification.action !== "deleted" && notification.entityId) {
    return { label: `Open ${route.noun}`, to: route.detail(notification.entityId) };
  }

  return { label: `View ${route.noun}${route.detail ? "s" : ""}`, to: route.list };
}
